import React from 'react';

import type { CaseTimelineEntry } from '../../lib/dossierTimeline';
import { useCaseFileStyle } from '../../context/CaseFileStyleContext';

interface CaseTimelineProps {
  entries: CaseTimelineEntry[];
  activeAnchorId?: string | null;
}

const KIND_STYLES: Record<CaseTimelineEntry['kind'], string> = {
  accountability_record: 'border-red-500/20 bg-red-500/5 text-red-500',
  activity: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-500',
  dossier_refresh: 'border-blue-500/20 bg-blue-500/5 text-blue-500',
  archive_snapshot: 'border-amber-500/20 bg-amber-500/5 text-amber-500',
};

export default function CaseTimeline({ entries, activeAnchorId }: CaseTimelineProps) {
  const { style } = useCaseFileStyle();
  const isEvidenceBoard = style === 'evidence-board';
  if (entries.length === 0) return null;

  return (
    <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-4">
        <div>
          <h3 className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mono">
            {isEvidenceBoard ? 'Exhibit Log' : 'Section — Case Timeline'}
          </h3>
          <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">One living sequence across evidence, public activity, dossier refreshes, and archival turnover.</p>
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300 mono">
          {isEvidenceBoard ? `Exhibits 01–${String(entries.length).padStart(2, '0')}` : `${entries.length} latest events`}
        </span>
      </div>

      <div className={`space-y-3 ${isEvidenceBoard ? 'relative pl-6' : ''}`}>
        {isEvidenceBoard && (
          <div
            className="absolute left-[7px] top-2 bottom-2 w-[2px]"
            style={{ backgroundImage: 'repeating-linear-gradient(180deg, #9B1C1C 0 6px, transparent 6px 11px)' }}
          />
        )}
        {entries.map((entry, i) => {
          const isActive = Boolean(entry.anchorId && activeAnchorId === entry.anchorId);
          const linkLabel = entry.external ? 'Open source' : entry.href?.startsWith('/officials/') ? 'View related dossier' : 'Jump to receipt';
          return (
            <article key={entry.id} className="relative">
              {isEvidenceBoard && (
                <div className="absolute -left-6 top-4 w-3 h-3 rounded-full bg-red-600 border-2 border-white dark:border-navy-800 shadow-[0_0_0_1px_#9B1C1C]" />
              )}
              <div
                className={`rounded-xl border px-4 py-4 ${isActive ? 'border-gold-500 bg-gold-500/10 shadow-lg shadow-gold-500/10' : 'border-cream-200 bg-cream-50 dark:border-navy-600/50 dark:bg-navy-900/20'}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {isEvidenceBoard && (
                        <span className="text-[9px] font-black text-navy-400 dark:text-navy-300 mono">Exhibit {String(i + 1).padStart(2, '0')}</span>
                      )}
                      <span className={`rounded-full border px-2 py-1 text-[9px] font-black uppercase tracking-widest ${KIND_STYLES[entry.kind]}`}>
                        {entry.badgeLabel}
                      </span>
                      {entry.isLatest && (
                        <span className="rounded-full border border-gold-500/30 bg-gold-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                          Latest
                        </span>
                      )}
                      <span className="text-[10px] font-bold uppercase tracking-widest text-navy-400 dark:text-navy-300">{entry.sourceLabel}</span>
                    </div>
                    <h4 className="mt-3 text-sm font-black uppercase tracking-wide text-navy-900 dark:text-cream-50">{entry.title}</h4>
                    <p className="mt-2 text-xs leading-relaxed text-navy-500 dark:text-cream-300">{entry.summary}</p>
                  </div>
                  <div className="shrink-0 sm:text-right">
                    <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">Timestamp</p>
                    <p className="mt-1 text-xs font-semibold text-navy-900 dark:text-cream-50">{entry.timestampLabel}</p>
                    {entry.href && (
                      <a
                        href={entry.href}
                        target={entry.external ? '_blank' : undefined}
                        rel={entry.external ? 'noopener noreferrer' : undefined}
                        className="mt-2 inline-flex text-xs font-semibold text-gold-600 hover:underline dark:text-gold-400"
                      >
                        {linkLabel}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
